import { Notice, Screen } from "./app-frame.js";
import { Skeleton } from "./components/skeleton.js";
import type { useBrands } from "./lib/queries/index.js";
import { SignedInContext } from "./lib/signed-in.js";

/**
 * Four states, and every one of them says something (§15a).
 *
 * The empty case is the one that matters. "No data available" would be true and
 * useless; a workspace with no brands needs to know that adding one is the next
 * step, not that the screen is broken.
 *
 * Out of app.tsx so that file stays the session, the workspace and the shell.
 */
export function BrandArea({
  workspaceId,
  brands,
  reportId,
}: {
  readonly workspaceId: string | null;
  readonly brands: ReturnType<typeof useBrands>;
  /** Chosen one level up, so the rail and the screens can never disagree. */
  readonly reportId: string | null;
}) {
  if (workspaceId === null) {
    return <Notice>Your account is not a member of any workspace yet.</Notice>;
  }

  if (brands.isPending) {
    return (
      <div className="page">
        <div className="container">
          <Skeleton shape="report" label="Loading the brands in this workspace" />
        </div>
      </div>
    );
  }

  if (brands.isError) {
    return (
      <Notice tone="error">
        Could not load this workspace&apos;s brands: {brands.error.message}. The captures
        behind them are unaffected — this is the list, not the data.
      </Notice>
    );
  }

  // Onboarding is decided one level up, before the masthead renders, so this
  // only ever runs for a workspace that is already measuring.
  if (brands.data[0] === undefined) {
    return <Notice>This workspace has no brands yet.</Notice>;
  }

  /*
   * The screen itself, from the router. A crash inside it is caught per screen
   * by the router, so the rail stays; while its code is still arriving the
   * page holds a skeleton rather than going blank.
   */
  return (
    <SignedInContext.Provider value={{ workspaceId, brands: brands.data, reportId }}>
      <Screen />
    </SignedInContext.Provider>
  );
}
